import {AppUserInterface} from "../AppUserInterface/AppUserInterface";
import {runSearch} from "../runSearch/runSearch";
import {loadShortNamesListAsPromise} from "./loadShortNameListAsPromis";
import {getMatchingNamesWithBoldedSearchedPattern} from "./getMatchingNames";
import {PhpDataProvider} from "../PhpDataProvider/PhpDataProvider";
import {AccentService} from "../AccentService/AccentService";

export class SearchWhispererHtmlElement extends HTMLElement
{
    template = `
			<style>
				#whisperer-container {
				    position: absolute;
				    min-width: 175px;
				    font-size: 12px;
                    color: black;
				    background-color: white;
				    border: 1px solid #c5c5c5;
				    list-style: none;
                    padding: 0;
                    margin: 0;
                    display: block;
                    outline: 0;
                    z-index: 100;
				}
				
				#whisperer-container li {
				    height: 20px;
				    display: block;
				    cursor: pointer;
				    margin: 0;
				    padding: 3px 1em 3px 0.4em;
				    /* Neviditelny border aby nedochazelo k poskakovani pro pridani borderu u hover. */
				    border: thin solid white; 
                }
                
                .activeItem {
                    color: black;
                    background: #95acbe;
                    border: thin solid #4A6C84;
                }
			</style>
			
			<ul id="whisperer-container"></ul>
		`;

    private shadow: ShadowRoot;

    private readonly phpData:             PhpDataProvider;
    private readonly userInterface:       AppUserInterface;
    private readonly accentService:       AccentService;

    private readonly container: HTMLUListElement;
    private activeItemIndex: number | null = null;

    private shortNamesList: string[] | undefined;

    constructor() {
        super();

        // Inicializace shadow DOMu.
        this.shadow = this.attachShadow({ mode: "open" });
        this.shadow.innerHTML = this.template;

        // Ziskani cointaineru a schovani.
        this.container = this.shadow.querySelector('ul#whisperer-container') as HTMLUListElement;
        this.container.style.display = 'none';

        // Potrebne instance trid.
        this.phpData             = new PhpDataProvider();
        this.userInterface       = new AppUserInterface();
        this.accentService = new AccentService(this.phpData.accentMap);

        // Poklud se klikne na jednu z moznosti naseptavace (nejaky li element). Vlozime text teto moznosti do
        //      vyhledavaciho pole, naseptavac schovame a spustime vyhledavani.
        this.container.addEventListener('click', (event: MouseEvent) => {
            if (! (event.target instanceof HTMLLIElement)) {
                return;
            }

            this.userInterface.searchBox.value = (event.target as HTMLLIElement).innerText;

            this.hideSuggestions();
            runSearch(
                this.userInterface,
                this.phpData.server,
                this.phpData.regionId,
                this.phpData.locale,
                this.phpData.pageSeoString
            );
        });

        // Poklud se mysi najede na jednu z moznosti naseptavace (nejaky li element). Nastavime tento element jako
        //      aktivni (obarveni elementu atd).
        this.container.addEventListener('mouseover', (event: MouseEvent) => {
            if (! (event.target instanceof HTMLLIElement)) {
                return;
            }

            this.setActiveItem(event.target as HTMLLIElement);
        });

        // Pokud mysi vyjedeme z containeru, tak zrusime aktivni element.
        this.container.addEventListener('mouseleave', () => {
            this.removeActiveItem();
        });

        // Pokud klikneme mimo vyhledavaci box, tak naseptavac schovame.
        document.addEventListener('click', (event: MouseEvent) => {
            if (! this.userInterface.searchBox.contains(event.target as HTMLElement)) {
                this.hideSuggestions();
            }
        });
    }

    // connectedCallback() {}
    // attributeChangedCallback(name: string, oldValue: string, newValue: string) {}

    public setPositionUnderSearchBar(): void {
        const rect = this.userInterface.searchBox.getBoundingClientRect();

        this.container.style.top = `${rect.top + window.scrollY + 25}px`;
        this.container.style.left = `${rect.left}px`;
    }

    public hideSuggestions(): void {
        this.container.style.display = 'none';
    }

    public showSuggestions(): void {
        this.container.style.display = 'block';
    }

    public setSuggestions(list: object): void {
        this.showSuggestions();

        Object.entries(list).forEach(([key, value]: [string, any]) => {
            if (typeof value !== 'string') {
                throw new Error('SearchWhispererElement::setSuggestions() Input list must contain only string!');
            }
        });

        // Odebereme vsechny nabizene moznosti naseptavace, tedy vsechny child elementy(li elementy).
        this.container.querySelectorAll('*').forEach((element: Element) => { element.remove() });

        // Vytvorime moznosti naseptavace, tedy child elementy(li elementy). A pridame je do containeru(ul elementu).
        Object.entries(list).forEach(([key, value]: [string, string]) => {
            const liElement = document.createElement('li');
            liElement.innerHTML = value;

            this.container.appendChild(liElement);
        });
    }


    public removeActiveItem(): void {
        if (this.activeItemIndex === null || this.container.children.item(this.activeItemIndex) === null) {
            return;
        }

        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.remove('activeItem');
        this.activeItemIndex = null;
    }


    public setActiveItem(liElement: HTMLLIElement): void {
        if (this.activeItemIndex !== null) {
            this.removeActiveItem();
        }

        this.activeItemIndex = Array.from(this.container.children).indexOf(liElement);
        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.add('activeItem');
    }


    public setNextItemAsActive(): void {
        // Odebrat class u soucasneho.
        if (this.activeItemIndex !== null) {
            (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.remove('activeItem');
        }

        // Nastavime dalsi prvek jako aktivni.
        this.activeItemIndex = (this.activeItemIndex !== null && this.activeItemIndex + 1 < this.container.children.length)
            ? this.activeItemIndex = this.activeItemIndex + 1
            : this.activeItemIndex = 0;

        // Pridat class u dalsiho nyni soucasneho.
        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.add('activeItem');

        // Nastavit text search baru aby odpovidal textu aktivniho elementu.
        this.userInterface.searchBox.value = (this.container.children.item(this.activeItemIndex) as HTMLLIElement).innerText;
    }

    public setPreviousItemAsActive(): void {
        // Odebrat class u soucasneho.
        if (this.activeItemIndex !== null) {
            (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.remove('activeItem');
        }

        // Nastavime predchozi prvek jako aktivni.
        this.activeItemIndex = (this.activeItemIndex !== null && this.activeItemIndex > 0)
            ? this.activeItemIndex = this.activeItemIndex - 1
            : this.activeItemIndex = this.container.children.length - 1;

        // Pridat class u dalsiho nyni soucasneho.
        (this.container.children.item(this.activeItemIndex) as HTMLLIElement).classList.add('activeItem');

        // Nastavit text search baru aby odpovidal textu aktivniho elementu.
        this.userInterface.searchBox.value = (this.container.children.item(this.activeItemIndex) as HTMLLIElement).innerText;
    }

    public showSuggestionsFor(searchedQuery: string): void {
        if (typeof this.shortNamesList === 'undefined') {
            const promiseOfShortNamesList = loadShortNamesListAsPromise(
                this.phpData.server,
                this.phpData.regionId,
                this.phpData.locale,
                this.phpData.pageSeoString
            );

            promiseOfShortNamesList
                .then(response => response.json())
                .then(data => {
                    this.shortNamesList = data as string[];

                    const suggestionsList = getMatchingNamesWithBoldedSearchedPattern(
                        this.accentService, searchedQuery, this.shortNamesList
                    );

                    this.setSuggestions(suggestionsList);
                });
            //.catch(() => {});
        } else {
            const suggestionsList = getMatchingNamesWithBoldedSearchedPattern(
                this.accentService, searchedQuery, this.shortNamesList
            );

            this.setSuggestions(suggestionsList);
        }
    }

}